<script lang="ts">
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import type {
    CancelAnnouncementPlansStepOutput,
    CancelReplanInit,
    CancelReplanMode,
    CancelReplanRow,
    CancelReplanWorkflowEvent,
  } from "../../../../_logic/cancel-replan-contract"
  import {
    buildActiveContactOrderByWorkOrderId,
    buildWorkOrderScheduleBatchRows,
    createStaticRowsRefreshKey,
  } from "../../../../_logic/rows"
  import {
    cancelReplanAnnouncementStatus,
    createAnnouncementPlanFilters,
    filterAnnouncementPlanRows,
    type AnnouncementPlanFilterState,
  } from "../../_shared/_logic/filters"
  import WorkOrderTableCell from "../../../work-order-table-cell"
  import BatchFilterBar from "../../../../../../../_shared/_modules/batch-filter-bar"
  import {
    batchDateRangeInputValue,
    hasUsefulBatchDateRange,
    hasUsefulBatchSearch,
    hasUsefulBatchSelectOptions,
    normalizeBatchDateRange,
    uniqueBatchOptions,
  } from "../../../../../../../_shared/_logic/filter-values"
  import {
    STABLE_ROW_NUMBER_COLUMN_ID,
    createStableRowNumberState,
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
  } from "../../../../../../../_shared/_logic/stable-row-numbers"
  import AnnouncementPlanActionRow from "./_modules/announcement-plan-action-row"
  import type {
    AnnouncementPlanActionPhase,
    AnnouncementPlanActionResult,
  } from "./actions/announcementPlans"
  import { buildAnnouncementPlanActionIntents } from "./build-announcement-plan-action-intents"
  import { announcementPlanOperations } from "./operations"

  let {
    init,
    mode = "createAnnouncementPlans",
    output,
    readOnly = false,
    onEvent,
    onError,
  }: {
    init: CancelReplanInit
    mode?: CancelReplanMode
    output?: CancelAnnouncementPlansStepOutput
    readOnly?: boolean
    onEvent: (event: CancelReplanWorkflowEvent) => void | Promise<void>
    onError: (message: string | null) => void
  } = $props()

  const propertyById = $derived(new Map(init.properties.map((property) => [property.id, property])))
  const workOrderRowById = $derived(new Map((init.sourceRows ?? []).map((row) => [row.id, row])))
  const eventByWorkOrderId = $derived(
    Object.fromEntries(
      init.events
        .filter((event): event is ScheduleEvent & { work_order_id: string } =>
          Boolean(event.work_order_id),
        )
        .map((event) => [event.work_order_id, event]),
    ),
  )
  const activeContactOrderByWorkOrderId = $derived(
    buildActiveContactOrderByWorkOrderId(init.contactOrders ?? []),
  )
  const rows = $derived(
    buildWorkOrderScheduleBatchRows({
      workOrders: init.workOrders,
      eventByWorkOrderId,
      workOrderRowById,
      propertyById,
      activeContactOrderByWorkOrderId,
    }).filter((row) => row.activeContactOrder),
  )
  let removedRowIds = $state<Set<string>>(new Set())
  let tableFilters = $state<AnnouncementPlanFilterState>(createAnnouncementPlanFilters())
  const rowNumberState = createStableRowNumberState()
  const displayRows = $derived(rows.map((row) => ({ ...row, removed: removedRowIds.has(row.id) })))
  const numberedDisplayRows = $derived(withStableRowNumbers(displayRows, rowNumberState))
  const selectedRows = $derived(numberedDisplayRows.filter((row) => !row.removed))
  const visibleRows = $derived(filterAnnouncementPlanRows(numberedDisplayRows, tableFilters))
  const refreshKey = $derived(createStaticRowsRefreshKey(visibleRows))
  const announcementStatusOptions = $derived(
    uniqueBatchOptions(selectedRows.map(cancelReplanAnnouncementStatus), "Alle Ankündigungen"),
  )
  const appointmentDateValues = $derived(
    selectedRows.map(
      (row) =>
        row.event?.start_at ??
        (row.event as { slot_start_at?: string | null } | null)?.slot_start_at,
    ),
  )

  const columns: ColumnDef[] = [
    stableRowNumberColumn(),
    { id: "orderSummary", label: "Auftrag", width: "180px" },
    { id: "statusLabel", label: "Termin", width: "210px" },
    { id: "propertyLabel", label: "Liegenschaft", width: "280px" },
    { id: "actionRequiredLabel", label: "Nächste Aktion", width: "220px" },
  ]
  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: typeof stableRowNumberSort }).sort

    return getStableSortedRowPage({
      rows: visibleRows,
      columns,
      sort,
      limit,
      offset,
    })
  }

  let actionPhase = $state<AnnouncementPlanActionPhase>("idle")
  const isDirectEndMode = $derived(mode === "cancelAnnouncementPlans")
  const isBusy = $derived(actionPhase !== "idle")
  const actionIntents = $derived(
    buildAnnouncementPlanActionIntents({ rows: selectedRows, directEndMode: isDirectEndMode }),
  )

  const stepIntro = $derived(
    isDirectEndMode
      ? "Prüfen Sie die aktiven Ankündigungspläne, die beendet werden sollen."
      : "Prüfen Sie die bestehenden Ankündigungspläne, die vor dem neuen Plan beendet werden.",
  )

  const removeRow = (rowId: string) => {
    if (readOnly) return

    removedRowIds = new Set([...removedRowIds, rowId])
  }

  const restoreRow = (rowId: string) => {
    if (readOnly) return

    const next = new Set(removedRowIds)
    next.delete(rowId)
    removedRowIds = next
  }

  type RowControlCommand = {
    kind: "remove" | "restore" | "reset"
    disabled?: boolean
    onClick?: () => void | Promise<void>
  }

  const rowControlsFor = (row: DataRow): RowControlCommand[] => {
    const item = row as CancelReplanRow

    if (item.removed) {
      return [
        {
          kind: "restore",
          disabled: readOnly || isBusy,
          onClick: () => restoreRow(item.id),
        },
      ]
    }

    return [
      {
        kind: "remove",
        disabled: readOnly || isBusy,
        onClick: () => removeRow(item.id),
      },
    ]
  }

  const updateFilter = (fieldId: string, value: unknown) => {
    tableFilters = {
      ...tableFilters,
      [fieldId]: fieldId === "dateRange" ? normalizeBatchDateRange(value) : String(value ?? ""),
    }
  }

  const handleActionResult = async (result: AnnouncementPlanActionResult) => {
    await onEvent({ type: "cancelAnnouncements.completed", output: result.output })
  }
</script>

<div class="step-body">
  <AnnouncementPlanActionRow
    intents={actionIntents}
    operations={announcementPlanOperations}
    onSubmissionResult={handleActionResult}
    onPhaseChange={(phase) => (actionPhase = phase)}
  >
    {#snippet children(primaryAction, footerActions)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Ankündigungspläne"
        count={visibleRows.filter((row) => !row.removed).length}
        description={stepIntro}
      >
        {#snippet filters()}
          <BatchFilterBar
            fields={[
              {
                id: "search",
                type: "text",
                placeholder: "Suche",
                value: tableFilters.search,
                enabled: hasUsefulBatchSearch(selectedRows.length),
              },
              {
                id: "announcementStatus",
                type: "select",
                placeholder: "Ankündigung",
                value: tableFilters.announcementStatus,
                options: announcementStatusOptions,
                enabled: hasUsefulBatchSelectOptions(
                  announcementStatusOptions,
                  selectedRows.length,
                ),
              },
              {
                id: "dateRange",
                type: "dateRange",
                placeholder: "Terminzeitraum",
                value: batchDateRangeInputValue(tableFilters.dateRange),
                enabled: hasUsefulBatchDateRange(appointmentDateValues, selectedRows.length),
              },
            ]}
            disabled={isBusy || readOnly}
            onChange={updateFilter}
          />
        {/snippet}

        {#if visibleRows.length}
          <Table
            {columns}
            {getRows}
            {refreshKey}
            defaultSort={stableRowNumberSort}
            theme="light"
            mode="table-scroll"
            pageSize={80}
            showActions={!readOnly}
            actionsHeaderLabel="Aktionen"
            rowControls={!readOnly ? rowControlsFor : undefined}
            getRowState={(row) => {
              const item = row as CancelReplanRow

              return {
                selectable: !item.removed,
                muted: item.removed,
                mutedReason: item.removed
                  ? "Diese Zeile wird in diesem Schritt ausgelassen."
                  : null,
              }
            }}
          >
            {#snippet tableCell({ row, column })}
              {@const item = row as CancelReplanRow}
              {#if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
                {item[STABLE_ROW_NUMBER_COLUMN_ID]}
              {:else if item.workOrderRow}
                <WorkOrderTableCell row={item.workOrderRow} {column} />
              {:else}
                —
              {/if}
            {/snippet}
          </Table>
        {:else}
          <p class="empty-hint">Es sind keine aktiven Ankündigungspläne in dieser Auswahl.</p>
        {/if}

        {#snippet footer()}
          {#if !readOnly}
            {@render footerActions()}
          {/if}
        {/snippet}
      </TableStepLayout>
    {/snippet}
  </AnnouncementPlanActionRow>

  {#if output}
    <p class="result">
      {output.canceled.length} abgebrochen, {output.completed.length} abgeschlossen, {output.skipped
        .length}
      übersprungen, {output.failed.length} fehlgeschlagen.
    </p>
  {/if}
</div>

<style lang="less">
  .step-body {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .result {
    font-weight: 800;
  }

  .empty-hint {
    padding: 0.8rem;
    border: 1px solid color-mix(in srgb, var(--text-100) 18%, transparent);
    background: color-mix(in srgb, var(--text-100) 4%, transparent);
  }
</style>
